import ConsultaiApiContext from "../consultai-api-context";
import {IConsultaiApiContext} from "../interfaces/IConsultaiApiContext";
import axios, {AxiosError, CreateAxiosDefaults} from "axios";
import {useContext, useEffect} from "react";
import {ConsultaiInterceptors} from "../../interceptors/consultai-interceptors";
import RequestError from "../../../models/services/common/request-error";
import {AuthenticationResponse} from "../../../models/services/responses/AuthenticationResponse";
import {useConsultaiAppContext} from "./useConsultaiApp";

// Configurações de resiliência
interface ResilienceConfig {
    maxRetries: number;
    initialDelayMs: number;
    maxDelayMs: number;
    retryStatusCodes: number[];
    timeoutMultiplier: number;
    baseTimeout: number;
    checkOnlineBeforeRetry: boolean;
}

const DEFAULT_RESILIENCE_CONFIG: ResilienceConfig = {
    maxRetries: 3,
    initialDelayMs: 300,
    maxDelayMs: 3000,
    retryStatusCodes: [
        408, // Timeout
        429, // Too Many Requests
        500, // Internal Server Error
        502, // Bad Gateway
        503, // Service Unavailable
        504], // Gateway Timeout
    timeoutMultiplier: 1.5,
    baseTimeout: 60000, // 60 segundos
    checkOnlineBeforeRetry: true
};

// Tipos de erros de rede
const NETWORK_ERROR_CODES = [
    'ECONNABORTED', // Timeout
    'ECONNRESET',   // Conexão resetada
    'ECONNREFUSED', // Conexão recusada
    'ETIMEDOUT',    // Timeout no nível TCP
    'ENETUNREACH',  // Rede inalcançável
    'ENETDOWN'      // Rede offline
];

const useConsultaiApi = (): IConsultaiApiContext => {
    const context = useContext(ConsultaiApiContext);
    const {setLoading} = useConsultaiAppContext();
    const API_CONFIG: CreateAxiosDefaults = {
        timeout: context?.timeout ?? 60000,
        baseURL: context?.baseUrl,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    }
    const apiClient = axios.create(API_CONFIG);
    const refreshTokenClient = axios.create({
        ...API_CONFIG,
        headers: {...API_CONFIG.headers, Authorization: `Bearer ${context?.accessToken}`}
    });
    const interceptors = ConsultaiInterceptors();
    const onRefreshToken = async (): Promise<string> => {
            const newAccessToken = (await refreshTokenClient.post<AuthenticationResponse>(`v${1}/autenticacao/refresh-token?refreshToken=${context?.refreshToken}`))?.data?.accessToken;
            context?.setAccessToken(newAccessToken);
            return `Bearer ${newAccessToken}`;
        }
    ;
    useEffect(() => {
        apiClient.interceptors.request.use((config) => interceptors.SEND_INTERCEPTOR(config, context?.accessToken, context?.env, context?.enableLogs));
        apiClient.interceptors.response.use(interceptors.ON_FULFILLED_INTERCEPTOR,
            (e) => interceptors.ON_REJECTED_INTERCEPTOR(e, context?.enableLogs, context?.env, onRefreshToken)
                .catch((error: RequestError) => onRequestError(error)));
    }, [context])
    const onRequestError = (error: RequestError) => {
        console.log({error})
        let message = error?.message || 'Verifique sua conexão e tente novamente.';
        if (message.includes('Exception')) message = 'Verifique os dados informados e tente novamente';
        context?.onError && context?.onError(message);
        if (!error?.shouldLogout) return;
        context?.setAccessToken(undefined);
        context?.setRefreshToken(undefined);
        context?.onError('Sessão expirada, faça login novamente.')
        context?.onLogout();
    }

    // Função para aguardar um tempo específico
    const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

    // Função para calcular o tempo de espera com exponential backoff
    const calculateBackoff = (attempt: number, initialDelay: number, maxDelay: number): number => {
        // Adiciona uma pequena variação aleatória para evitar "thundering herd"
        const jitter = Math.random() * 100;
        const backoff = initialDelay * Math.pow(2, attempt) + jitter;
        return Math.min(backoff, maxDelay);
    };

    // Verifica se o dispositivo está online
    const isOnline = (): boolean => {
        return typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' 
            ? navigator.onLine 
            : true; // Se não conseguir verificar, assume que está online
    };

    // Verifica se o erro é relacionado à rede
    const isNetworkError = (error: AxiosError): boolean => {
        // Verifica se é timeout
        if (error.code && NETWORK_ERROR_CODES.includes(error.code)) {
            return true;
        }
        
        // Verifica se não há resposta (geralmente indica problema de rede)
        if (!error.response) {
            return true;
        }
        
        return false;
    };

    // Função que executa a requisição com retries
    const executeWithRetries = async <R>(
        requestFn: (timeout?: number) => Promise<R>, 
        config: ResilienceConfig = DEFAULT_RESILIENCE_CONFIG
    ): Promise<R> => {
        let lastError: any;
        let currentTimeout = config.baseTimeout;
        
        for (let attempt = 0; attempt < config.maxRetries + 1; attempt++) {
            try {
                // Se configurado para verificar conexão e não for a primeira tentativa
                if (attempt > 0 && config.checkOnlineBeforeRetry && !isOnline()) {
                    // Aguarda até 10 segundos verificando conexão a cada segundo
                    for (let i = 0; i < 10; i++) {
                        await delay(1000);
                        if (isOnline()) break;
                    }
                    
                    // Se ainda estiver offline após aguardar
                    if (!isOnline()) {
                        const offlineError = new Error('Por favor, verifique sua conexão com a internet e tente novamente.');
                        throw offlineError;
                    }
                }
                
                // Aumenta o timeout progressivamente a cada tentativa
                return await requestFn(currentTimeout);
            } catch (error) {
                lastError = error;
                
                // Aumenta o timeout para a próxima tentativa
                currentTimeout = Math.min(
                    currentTimeout * config.timeoutMultiplier,
                    config.baseTimeout * 3 // Limite máximo de 3x o timeout base
                );
                
                const axiosError = error as AxiosError;
                const status = axiosError?.response?.status;
                
                // Verifica se é erro de rede ou status code que deve ser retentado
                const isNetworkErr = isNetworkError(axiosError);
                const isRetryableStatus = status && config.retryStatusCodes.includes(status);
                const shouldRetry = isNetworkErr || isRetryableStatus;
                
                if (!shouldRetry || attempt === config.maxRetries) {
                    break;
                }
                
                // Calcula tempo de espera e aguarda antes da próxima tentativa
                const delayTime = calculateBackoff(attempt, config.initialDelayMs, config.maxDelayMs);
                
                if (context?.enableLogs) {
                    const errorType = isNetworkErr ? 'Erro de rede' : `Status ${status}`;
                    console.info(`${errorType}: Requisição falhou, tentando novamente em ${delayTime}ms (tentativa ${attempt + 1}/${config.maxRetries}) com timeout de ${currentTimeout}ms`);
                }
                
                await delay(delayTime);
            }
        }
        
        // Se chegou aqui, todas as tentativas falharam
        throw lastError;
    };

    const makeRequest = async <R>(method: 'get' | 'post' | 'put' | 'patch' | 'delete', url: string, version = 1, data?: any): Promise<R> => {
        try {
            setLoading(true);
            const fullUrl = `v${version}${url}`;
            const baseTimeout = context?.timeout ?? 60000;
            
            // Função que executa a requisição real
            const executeRequest = async (timeout?: number): Promise<any> => {
                const config = {
                    timeout: timeout || baseTimeout,
                    baseURL: context?.baseUrl,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        Authorization: `Bearer ${context?.accessToken}`
                    }
                };
                
                let response;
                switch (method) {
                    case 'get':
                        response = await apiClient[method]<R>(fullUrl, {...config, params: data});
                        break;
                    case 'delete':
                        response = await apiClient[method]<R>(fullUrl, {...config, data});
                        break;
                    case 'post':
                    case 'put':
                    case 'patch':
                        response = await apiClient[method]<R>(fullUrl, data, config);
                        break;
                }
                return response?.data;
            };
            
            // Executa a requisição com resiliência
            const result = await executeWithRetries(executeRequest, {
                ...DEFAULT_RESILIENCE_CONFIG,
                baseTimeout
            });
            
            setLoading(false);
            return result;
        } catch (e) {
            // Tratamento específico para erro de dispositivo offline
            if (e?.message === 'Por favor, verifique sua conexão com a internet e tente novamente.') {
                context?.onError && context?.onError(e.message);
                return Promise.reject(new RequestError(e.message, 0));
            }
            
            onRequestError(new RequestError(e?.response?.data, e?.response?.status));
            return Promise.reject(new RequestError(e?.response?.data, e?.response?.status))
        } finally {
            setLoading(false);
        }
    };

    const post = <R>(url: string, version = 1, data?: any) => makeRequest<R>('post', url, version, data);
    const get = <R>(url: string, version = 1, params?: any) => makeRequest<R>('get', url, version, params);
    const put = <R>(url: string, version = 1, data?: any) => makeRequest<R>('put', url, version, data);
    const patch = <R>(url: string, version = 1, data?: any) => makeRequest<R>('patch', url, version, data);
    const remove = <R>(url: string, version = 1, data?: any) => makeRequest<R>('delete', url, version, data ?? {});

    return {
        instance: apiClient,
        onLogout: context?.onLogout,
        accessToken: context?.accessToken,
        setAccessToken: context?.setAccessToken,
        refreshToken: context?.refreshToken,
        setRefreshToken: context?.setRefreshToken,
        env: context?.env,
        baseUrl: context?.baseUrl,
        enableLogs: context?.enableLogs,
        onError: context?.onError,
        onWarn: context?.onWarn,
        onInfo: context?.onInfo,
        timeout: context?.timeout,
        setBaseUrl: context?.setBaseUrl,
        setEnv: context?.setEnv,
        setEnableLogs: context?.setEnableLogs,
        setTimeout: context?.setTimeout,
        post,
        get,
        put,
        patch,
        delete: remove,
    };
};

export default useConsultaiApi;